import { ReactNode, useRef, useState } from "react";
import { css } from "@emotion/react";
import { Colors, FloatingBox, Typography } from "@zapier/design-system";
import { useStateRef } from "./useStateRef";

const TRANSITION = "100ms ease-in-out 0s";

const Styles = {
  root: css({
    width: "100%",
  }),

  trackContainer: css({
    height: "4px",
    position: "relative",
    width: "100%",
    zIndex: 2,

    "&::before": {
      content: '""',
      top: "-10px",
      left: 0,
      right: 0,
      bottom: "-12px",
      position: "absolute",
      display: "block",
    },
  }),

  track: css({
    backgroundColor: Colors.neutral400,
    borderRadius: "2px",
    overflow: "hidden",
    position: "absolute",
    top: 0,
    right: 0,
    left: 0,
    bottom: 0,
  }),

  mercury: css({
    backgroundColor: Colors.blue600,
    left: 0,
    height: "100%",
    pointerEvents: "none",
    position: "absolute",
    top: 0,
    transition: `width ${TRANSITION}`,
  }),

  thumb: css({
    backgroundColor: Colors.blue600,
    borderRadius: "50%",
    cursor: "grab",
    height: "24px",
    left: 0,
    position: "absolute",
    top: "2px",
    transition: TRANSITION,
    transitionProperty: "left, transform",
    transform: "translate(-50%, -50%)",
    transformOrigin: "center",
    width: "24px",

    "&::before": {
      borderRadius: "50%",
      content: "''",
      position: "absolute",
      pointerEvents: "none",
      top: 0,
      right: 0,
      bottom: 0,
      left: 0,
      border: "2px solid transparent",
      transition: "all ease-in-out 200ms",
    },

    "&:focus": {
      "&::before": {
        borderColor: Colors.blue600,
        top: "-4px",
        right: "-4px",
        left: "-4px",
        bottom: "-4px",
      },
    },

    "&:active": {
      cursor: "grabbing",
    },

    "&:hover, &:active": {
      transform: "translate(-50%, -50%) scale(1.1)",
    },
  }),

  dragging: css({
    transition: "none",
  }),

  steps: css({
    display: "grid",
    gridAutoFlow: "column",
    gridAutoColumns: "1fr",
    position: "relative",
  }),

  step(showTick: boolean) {
    return css({
      position: "relative",
      zIndex: 1,

      "&::before": {
        content: '""',
        backgroundColor: Colors.neutral400,
        height: "8px",
        width: "3px",
        display: showTick ? "block" : "none",
      },

      "&:last-child": {
        position: "absolute",
        right: 0,
      },
    });
  },

  stepActive: css({
    "&::before": {
      backgroundColor: Colors.blue600,
    },
  }),

  stepLabel: css({
    color: Colors.neutral800,
    marginTop: "8px",
    transform: "translateX(-50%)",
    textAlign: "center",
    ...Typography.smallPrint3Semibold,
  }),

  stepTooltip: css({
    padding: "16px 19px",
    whiteSpace: "nowrap",
    ...Typography.paragraph3Medium,
  }),
};

export type SliderStep = {
  label?: string | null;
  showLabel?: boolean;
  showTick?: boolean;
  tooltip?: string | ReactNode;
};

export type SliderProps = {
  ariaLabel: string;
  className?: string;
  min?: number;
  max?: number;
  step?: number;
  steps?: Array<SliderStep>;
  value?: number;

  onChange?: (value: number) => void;
  onInput?: (value: number) => void;
};

export const Slider = ({
  ariaLabel,
  className,
  min = 0,
  max = 100,
  step = 1,
  steps = [],
  value = 0,

  onChange,
  onInput,
}: SliderProps) => {
  // Hooks
  const trackRef = useRef<HTMLDivElement>(null);
  const thumbRef = useRef<HTMLDivElement>(null);

  // State
  const [isDragging, setIsDragging] = useState<boolean | null>(null);
  const [draggingValue, setDraggingValue] = useState(0);
  const {
    state: internalValue,
    stateRef: internalValueRef,
    setValue: setInternalValue,
  } = useStateRef(value);

  const numberOfSteps = (max - min + 1) / step;

  // Event handlers
  function onKeyDown(event: React.KeyboardEvent<HTMLDivElement>) {
    const isPageKey = ["PageUp", "PageDown"].includes(event.key);
    const multiplier = event.shiftKey || isPageKey ? 10 : 1;

    switch (event.key) {
      case "ArrowLeft":
      case "ArrowDown":
      case "PageDown":
        return setValue(internalValue - step * multiplier);
      case "ArrowUp":
      case "ArrowRight":
      case "PageUp":
        return setValue(internalValue + step * multiplier);
      case "Home":
        return setValue(min);
      case "End":
        return setValue(max);
    }
  }

  function onPointerDown(initialEvent: React.PointerEvent<HTMLDivElement>) {
    if (trackRef.current === null) return;
    const rect = trackRef.current.getBoundingClientRect();
    let value = 0;
    setIsDragging(true);

    function onPointerMove(
      event: PointerEvent | React.PointerEvent<HTMLDivElement>
    ) {
      event.preventDefault();

      const relativeX = event.clientX - rect.left;
      const percentage = relativeX / rect.width;
      value = percentage * max;

      setValue(value, false);
    }

    function onPointerUp() {
      setIsDragging(false);
      setValue(value, true);

      document.removeEventListener("pointermove", onPointerMove);
      document.removeEventListener("pointerup", onPointerUp);
      if (thumbRef.current) thumbRef.current.focus();
    }

    document.addEventListener("pointermove", onPointerMove);
    document.addEventListener("pointerup", onPointerUp);

    // Call it at least once in case they never move their mouse again
    onPointerMove(initialEvent);
  }

  // Methods
  function setValue(desiredValue: number, emitChange = true) {
    const rawValue = Math.max(min, Math.min(max, desiredValue));
    const isCloseToFloor = rawValue % step <= step * 0.2; // Within 20% of floor, round down
    const snappedValue = isCloseToFloor
      ? Math.floor(rawValue)
      : Math.ceil(rawValue);

    setDraggingValue(rawValue);

    if (internalValueRef.current !== snappedValue && onInput) {
      onInput(snappedValue);
    }

    setInternalValue(snappedValue);

    if (emitChange && onChange) onChange(snappedValue);
  }

  // Computed values
  const activeValue = isDragging ? draggingValue : internalValue;
  const thumbPosition = activeValue / max;

  return (
    <div css={Styles.root} className={className}>
      <div css={Styles.trackContainer} onPointerDown={onPointerDown}>
        <div css={Styles.track} ref={trackRef}>
          <div
            css={[Styles.mercury, isDragging && Styles.dragging]}
            style={{
              width: `${thumbPosition * 100}%`,
            }}
          />
        </div>

        <div
          css={[Styles.thumb, isDragging && Styles.dragging]}
          ref={thumbRef}
          style={{
            left: `${thumbPosition * 100}%`,
          }}
          aria-label={ariaLabel}
          aria-valuemin={min}
          aria-valuemax={max}
          aria-valuenow={internalValue}
          aria-valuetext={steps[internalValue]?.label || ""}
          role="slider"
          tabIndex={0}
          onKeyDown={onKeyDown}
        />
      </div>

      <div css={Styles.steps}>
        {Array.from(Array(numberOfSteps).keys()).map((index) => {
          const stepMeta = steps[index];
          const isActive = activeValue >= index * step;

          return (
            <div
              key={index}
              css={[
                Styles.step(stepMeta?.showTick ?? false),
                isActive && Styles.stepActive,
              ]}
            >
              <div css={Styles.stepLabel}>
                {stepMeta.showLabel !== false && stepMeta?.label}
                {stepMeta.tooltip && isActive && (
                  <FloatingBox align="right" minWidth="275px" position="south">
                    <div css={Styles.stepTooltip}>{stepMeta.tooltip}</div>
                  </FloatingBox>
                )}
              </div>
            </div>
          );
        })}
      </div>
    </div>
  );
};
